import React, { useMemo, useState } from "react";
import {
    Box,
    CircularProgress,
    Link,
    Paper,
    Typography
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { mergeStyles } from "@fluentui/react";
import { Search20Regular } from "@fluentui/react-icons";
import { CustomDataGridToolbar } from "../../../SharedComponents/CustomDataGrid/CustomDataGridToolbar";
import {
    RichSearchBox,
    CTLFilterPill,
    CTLSearchableFilterPill,
    CTLDateRangeFilterPill,
    ShareFilterButton
} from "../utils/CTLRichControls";
import CTLExploreDataButton from "../utils/CTLExploreDataButton";
import { CTL_DCR_REQUESTS_QUERY } from "../utils/ctlKustoQueries";
import { THEME } from "../utils/theme";
import { IDCRRequest } from "../../../interfaces/CloseTheLoop.interfaces";
import { useGetDCRRequestsQuery } from "../../../slices/CloseTheLoop.slice";

type DCRGridRow = IDCRRequest & { rowId: string };

const displayValue = (value?: string) => value?.trim() || "—";

const DCRRequests: React.FC<{ onOpenDetails: (featureRequestIdWithSource: string) => void }> = ({ onOpenDetails }) => {
    const { data, isLoading, isFetching, isError } = useGetDCRRequestsQuery();
    const [searchText, setSearchText] = useState("");
    const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
    const [createdDateRange, setCreatedDateRange] = useState<{ from: string; to: string }>({ from: "", to: "" });

    const onFilterChange = (filterKey: string, values: string[]) => {
        setAppliedFilters((prev) => ({ ...prev, [filterKey]: values }));
    };

    // Case Number and Feature Request Id aren't returned as filter enums (too many),
    // so derive the distinct lists from the loaded requests for the searchable pills.
    const caseNumberOptions = useMemo(() => {
        const values = new Set<string>();
        (data?.requests || []).forEach((request) => {
            if (request.caseNumber?.trim()) {
                values.add(request.caseNumber.trim());
            }
        });
        return Array.from(values).sort();
    }, [data?.requests]);

    const featureRequestIdOptions = useMemo(() => {
        const values = new Set<string>();
        (data?.requests || []).forEach((request) => {
            if (request.featureRequestId?.trim()) {
                values.add(request.featureRequestId.trim());
            }
        });
        return Array.from(values).sort();
    }, [data?.requests]);

    const rows = useMemo<DCRGridRow[]>(() => {
        const term = searchText.trim().toLowerCase();
        const matchesFilter = (key: string, value?: string) => {
            const selected = appliedFilters[key];
            return !selected || selected.length === 0 || (!!value && selected.includes(value));
        };
        const matchesCreatedDate = (value?: string) => {
            if (!createdDateRange.from && !createdDateRange.to) return true;
            if (!value) return false;
            const created = new Date(value);
            if (Number.isNaN(created.getTime())) return false;
            if (createdDateRange.from && created < new Date(`${createdDateRange.from}T00:00:00`)) return false;
            if (createdDateRange.to && created > new Date(`${createdDateRange.to}T23:59:59`)) return false;
            return true;
        };

        return (data?.requests || [])
            .filter((request) => {
                const matchesSearch = !term || [
                    request.dcrRequestId,
                    request.caseNumber,
                    request.title,
                    request.tenantName,
                    request.productName,
                    request.submittingOrganization,
                    request.ownerName,
                    request.featureRequestId
                ].some((value) => value?.toLowerCase().includes(term));

                return matchesSearch &&
                    matchesFilter("productName", request.productName) &&
                    matchesFilter("tenantName", request.tenantName) &&
                    matchesFilter("triageStatus", request.portalTriageStatus) &&
                    matchesFilter("submittingOrganization", request.submittingOrganization) &&
                    matchesFilter("owner", request.ownerName) &&
                    matchesFilter("caseNumber", request.caseNumber) &&
                    matchesFilter("featureRequestId", request.featureRequestId) &&
                    matchesCreatedDate(request.createdDate);
            })
            .map((request, index) => ({
                ...request,
                rowId: `${request.dcrRequestId || "dcr"}-${request.caseNumber || "case"}-${index}`
            }));
    }, [appliedFilters, createdDateRange, data?.requests, searchText]);

    const columns = useMemo<GridColDef<DCRGridRow>[]>(() => [
        { field: "dcrRequestId", headerName: "DCR ID", width: 130 },
        { field: "title", headerName: "DCR Title", minWidth: 220, flex: 1 },
        { field: "productName", headerName: "Product", width: 180 },
        { field: "tenantName", headerName: "Customer", width: 200 },
        {
            field: "featureRequestId",
            headerName: "FR ID",
            width: 150,
            renderCell: (params: GridRenderCellParams<DCRGridRow>) => {
                const frId = params.row.featureRequestId?.trim();
                const frIdWithSource = params.row.featureRequestIdWithSource?.trim();
                if (!frId || !frIdWithSource) return displayValue(frId);
                return (
                    <Link
                        component="button"
                        type="button"
                        onClick={(event) => {
                            event.stopPropagation();
                            onOpenDetails(frIdWithSource);
                        }}
                        sx={{ fontSize: "12px", fontWeight: 500, textAlign: "left" }}
                    >
                        {frId}
                    </Link>
                );
            }
        },
        { field: "caseNumber", headerName: "Case Number", width: 140 },
        { field: "submittingOrganization", headerName: "Submitting Organization", width: 190 },
        { field: "ownerName", headerName: "Owner", width: 160 },
        { field: "portalTriageStatus", headerName: "Portal / Triage Status", width: 170 },
        { field: "dcrTriageNotes", headerName: "DCR Triage Notes", width: 200 },
        { field: "rejectionReason", headerName: "Rejection Reason", width: 180 }
    ], [onOpenDetails]);

    if (isError) {
        return (
            <Paper
                variant="outlined"
                sx={{ p: 4, borderColor: THEME.border, textAlign: "center" }}
            >
                <Typography sx={{ color: THEME.error, fontWeight: 600 }}>
                    DCR requests could not be loaded.
                </Typography>
                <Typography sx={{ color: THEME.textSecondary, fontSize: 13, mt: 0.5 }}>
                    Refresh the page to try again.
                </Typography>
            </Paper>
        );
    }

    if (isLoading && !data) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 2 }}>
                <CircularProgress size={40} sx={{ color: THEME.primary }} />
                <Typography sx={{ color: THEME.textSecondary, fontSize: "14px" }}>Loading DCR requests...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
            {/* Rich Search Bar */}
            <RichSearchBox
                value={searchText}
                onChange={setSearchText}
                onClear={() => setSearchText("")}
                placeholder="Search by DCR ID, DCR Title, Customer, Product, Case Number, or Feature Request ID"
                showHint={searchText.length > 0 && searchText.length < 3}
            />

            {/* Filters Row */}
            <div className={mergeStyles({ margin: "-4px", display: "flex", flexWrap: "wrap", paddingBottom: 16, alignItems: "center" })}>
                <CTLFilterPill
                    name="Product"
                    options={data?.filters.productNames || []}
                    selectedValues={appliedFilters.productName}
                    onApply={(values) => onFilterChange("productName", values || [])}
                    urlParamKey="dcrProduct"
                />
                <CTLSearchableFilterPill
                    name="Customer"
                    options={data?.filters.tenantNames || []}
                    selectedValues={appliedFilters.tenantName}
                    onApply={(values) => onFilterChange("tenantName", values || [])}
                    maxVisibleItems={50}
                />
                <CTLFilterPill
                    name="Triage Status"
                    options={data?.filters.triageStatuses || []}
                    selectedValues={appliedFilters.triageStatus}
                    onApply={(values) => onFilterChange("triageStatus", values || [])}
                    urlParamKey="dcrTriageStatus"
                />
                <CTLSearchableFilterPill
                    name="Case Number"
                    options={caseNumberOptions}
                    selectedValues={appliedFilters.caseNumber}
                    onApply={(values) => onFilterChange("caseNumber", values || [])}
                    maxVisibleItems={50}
                />
                <CTLSearchableFilterPill
                    name="Feature Request ID"
                    options={featureRequestIdOptions}
                    selectedValues={appliedFilters.featureRequestId}
                    onApply={(values) => onFilterChange("featureRequestId", values || [])}
                    maxVisibleItems={50}
                />
                <CTLDateRangeFilterPill
                    name="Created Date"
                    onApply={(fromDate, toDate) => setCreatedDateRange({ from: fromDate, to: toDate })}
                />
                <CTLSearchableFilterPill
                    name="Submitting Organization"
                    options={data?.filters.submittingOrganizations || []}
                    selectedValues={appliedFilters.submittingOrganization}
                    onApply={(values) => onFilterChange("submittingOrganization", values || [])}
                    maxVisibleItems={50}
                />
                <CTLSearchableFilterPill
                    name="Owner"
                    options={data?.filters.owners || []}
                    selectedValues={appliedFilters.owner}
                    onApply={(values) => onFilterChange("owner", values || [])}
                    maxVisibleItems={50}
                />
                <ShareFilterButton />
            </div>

            {/* Data Grid */}
            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${THEME.border}`,
                    borderRadius: "8px",
                    overflow: "hidden",
                    flex: 1,
                    minHeight: 200,
                    maxHeight: "calc(100vh - 450px)",
                    display: "flex",
                    flexDirection: "column",
                    "& .MuiDataGrid-root": {
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        border: "none"
                    },
                    "& .MuiDataGrid-main": {
                        flex: 1,
                        minHeight: 0
                    },
                    "& .MuiDataGrid-footerContainer": {
                        minHeight: 52,
                        flexShrink: 0,
                        borderTop: `1px solid ${THEME.border}`
                    }
                }}
            >
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.rowId}
                    loading={isLoading || isFetching}
                    density="compact"
                    disableRowSelectionOnClick
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    slots={{
                        toolbar: () => (
                            <CustomDataGridToolbar
                                filterModel={{ items: [] }}
                                toolbarConfig={{ disableGridToolbarQuickFilter: false }}
                                exportCsvButtonProps={{
                                    buttonLabel: "Export",
                                    fileNamePrefix: "dcr-requests"
                                }}
                            />
                        ),
                        loadingOverlay: () => (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", width: "100%", backgroundColor: "rgba(255, 255, 255, 0.95)" }}>
                                <CircularProgress size={40} sx={{ color: THEME.primary, mb: 2 }} />
                                <Typography sx={{ color: THEME.textPrimary, fontSize: "14px", fontWeight: 500 }}>Loading data...</Typography>
                            </Box>
                        ),
                        noRowsOverlay: () => (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", py: 4, background: "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(250,251,252,1) 100%)" }}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 320 }}>
                                    <Box sx={{ width: 80, height: 80, borderRadius: "20px", background: "linear-gradient(135deg, #F0F4FF 0%, #E8F0FE 100%)", display: "flex", alignItems: "center", justifyContent: "center", mb: 3, boxShadow: "0 4px 12px rgba(0, 90, 158, 0.08)" }}>
                                        <Search20Regular style={{ color: THEME.primary, width: 36, height: 36, opacity: 0.8 }} />
                                    </Box>
                                    <Typography sx={{ color: THEME.textPrimary, fontSize: "16px", fontWeight: 600, mb: 1 }}>No Results Found</Typography>
                                    <Typography sx={{ color: THEME.textSecondary, fontSize: "13px", textAlign: "center", lineHeight: 1.5 }}>We couldn't find any DCR requests matching your criteria. Try adjusting your filters or search terms.</Typography>
                                </Box>
                            </Box>
                        )
                    }}
                    sx={{
                        border: "none",
                        fontSize: "12px",
                        "& .MuiDataGrid-columnHeaders": { backgroundColor: "#F8F9FA", borderBottom: `1px solid ${THEME.border}` },
                        "& .MuiDataGrid-columnHeaderTitle": { fontSize: "12px", fontWeight: 600 },
                        "& .MuiDataGrid-cell": { borderBottom: `1px solid ${THEME.border}`, fontSize: "12px" },
                        "& .MuiDataGrid-row": { cursor: "pointer" },
                        "& .MuiDataGrid-row:hover": { backgroundColor: `${THEME.primary}08` },
                        "& .MuiDataGrid-menuIconButton": { color: "white" },
                        "& .MuiDataGrid-overlayWrapper": { backgroundColor: "rgba(255, 255, 255, 0.98)" }
                    }}
                />
            </Paper>

            {/* Explore Data Button Footer - Matches CustomCard footer style */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    minHeight: 45,
                    borderTop: `2px solid ${THEME.border}`,
                    backgroundColor: THEME.surface,
                    borderRadius: "0 0 8px 8px",
                    border: `1px solid ${THEME.border}`,
                    borderTopWidth: 0,
                    px: 1.5
                }}
            >
                <CTLExploreDataButton
                    query={CTL_DCR_REQUESTS_QUERY}
                    chartTitle="DCR Requests Table"
                />
            </Box>
        </Box>
    );
};

export default DCRRequests;
